// Phase 33 · 19b — Tickets, the register.
//
// Ordered by SLA deadline, so the breached rows sit at the top by consequence
// rather than by date, and the sort arrow sits on the column the rows are
// actually ordered by. The cohort tabs are not a second filtering system: each
// one writes ordinary values into the SAME `TicketFilters` the advanced panel
// edits (see ticketsQueue.ts), so a tab and the panel can never disagree.
//
// Nothing the old screen could do was dropped: search, the advanced filter
// panel, the default saved filter, the pipeline selector, export, the board
// toggle, refresh, pagination, bulk actions and the merge modal all survive —
// the shared TicketToolbar was left untouched because the Board still uses it.

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Mail,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  LayoutGrid,
  RotateCw,
  SlidersHorizontal,
  Bookmark,
  X,
  GitBranch,
} from 'lucide-react';
import { clsx } from 'clsx';
import { ConfirmDialog, useToast } from '@centraoneplus/ui';
import { Pager } from '@shared/ui/kit';
import { useQuery } from '@tanstack/react-query';
import { useTicketsListStore } from '@stores/ticketsListStore';
import { useSavedFiltersStore } from '@stores/savedFiltersStore';
import { useAuthStore } from '@stores/authStore';
import { pipelinesSettingsTarget } from '../settings/pipelines-target';
import { canManageAssignees } from './assignmentPermissions';
import { secondaryBadgeLabel } from './secondaryAgents';
import {
  AdvancedFilters,
  buildApiFilters,
  FILTER_RESET_KEYS,
  type TicketFilters,
} from '@components/tickets/AdvancedFilters';
import { BulkActionsBar, SelectCheckbox } from '@components/tickets/BulkActionsBar';
import { ticketService } from '@services/ticket.service';
import { TicketMergeModal } from '@components/tickets/TicketMergeModal';
import { PriorityBadge } from '@components/tickets/PriorityBadge';
import { TicketSourceBadge, getTicketSourceDisplay } from '@components/tickets/TicketSourceBadge';
import { initials, identityColor, personName } from '@components/tickets/identity';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ExportButton } from '@components/common/ExportButton';
import { ExportColumn, formatters } from '@utils/export';
import { pipelineService } from '@/services/pipeline.service';
import type { Pipeline } from '@/types/pipeline';
import { PipelineSelect } from '@components/tickets/PipelineSelect';
import type { Ticket } from '@centraoneplus/shared-types';
import {
  TAB_LABEL,
  TICKET_TABS,
  activeTab,
  applyTab,
  customerLabel,
  filtersFromSearchParams,
  liveSla,
  pipelineStage,
  type TicketSlaTone,
  type TicketTab,
} from './ticketsQueue';
import { rememberTicketsView } from '@/lib/lastTicketsView';

// Sentinel for the list-view "All Pipelines" option (board view has no such option).
const ALL_PIPELINES = 'all';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const HEADER_CARD: React.CSSProperties = { borderRadius: 14, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90';
const HEADER_PRIMARY =
  'flex h-[34px] flex-none items-center rounded-[10px] bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90';

const TONE_INK: Record<TicketSlaTone, string> = {
  danger: 'var(--co-burgundy)',
  warn: 'var(--co-terracotta-ink)',
  normal: 'var(--co-ink-2)',
  none: 'var(--co-ink-4)',
};

/**
 * Channel is shown as mono text in the subject's metadata line, not as a
 * colored icon column. A tinted mail glyph repeated forty times down a column
 * is noise, and the word is unambiguous where the glyph isn't.
 */
const CHANNEL_LABELS: Record<string, string> = {
  email: 'EMAIL',
  chat: 'CHAT',
  phone: 'VOICE',
  voice: 'VOICE',
  web: 'WEB',
};

function SortableHeader({
  field,
  label,
  className,
  /**
   * `sla_priority` has ONE direction on the server (firstResponseDueAt ascending,
   * nulls last) — it ignores sortOrder entirely. Letting the header toggle would
   * flip an arrow while the rows stayed put, which is a header lying about the
   * order beneath it.
   */
  ascOnly,
}: {
  field: string;
  label: string;
  className?: string;
  ascOnly?: boolean;
}) {
  const { filters, setSorting, setFilters, fetchTickets } = useTicketsListStore();
  const isActive = filters.sortBy === field;

  const onClick = () => {
    if (!ascOnly) {
      setSorting(field);
      return;
    }
    if (isActive) return;
    setFilters({ sortBy: field, sortOrder: 'asc' });
    fetchTickets();
  };

  return (
    <th
      className={`px-4 pb-[11px] pt-[14px] text-left align-middle whitespace-nowrap ${className || ''}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="co-mono flex items-center gap-1 text-[9.5px] font-semibold uppercase leading-none tracking-[.2em] text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:text-[var(--co-ink)]"
        style={isActive ? { color: 'var(--co-ink)' } : undefined}
      >
        {label}
        {isActive ? (
          filters.sortOrder === 'asc' || ascOnly ? (
            <ChevronUp className="h-3 w-3 text-[var(--co-brass)]" />
          ) : (
            <ChevronDown className="h-3 w-3 text-[var(--co-brass)]" />
          )
        ) : (
          <ArrowUpDown className="h-[10px] w-[10px] opacity-30" />
        )}
      </button>
    </th>
  );
}

/** Plain (non-sortable) column header, same voice. */
function PlainHeader({ label, className }: { label: string; className?: string }) {
  return (
    <th
      className={`co-mono px-4 pb-[11px] pt-[14px] text-left align-middle whitespace-nowrap text-[9.5px] font-semibold uppercase leading-none tracking-[.2em] text-[var(--co-ink-3)] ${className || ''}`}
    >
      {label}
    </th>
  );
}

interface TicketRowProps {
  ticket: Ticket;
  onClick: () => void;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
}

function TicketRow({ ticket, onClick, isSelected, onSelect }: TicketRowProps) {
  const sla = liveSla(ticket);
  const channel = CHANNEL_LABELS[ticket.channel] || String(ticket.channel || '').toUpperCase();
  const isArchived = ticket.status === 'closed';
  const who = customerLabel(ticket);
  const lane = pipelineStage(ticket);
  const assignee = personName((ticket as any).assignee?.name) || undefined;
  const currentUserId = useAuthStore((st) => st.user?.id);
  const secondaryBadge = secondaryBadgeLabel(ticket, currentUserId);

  // Selection wins the left marker; otherwise SLA urgency owns it. The marker
  // is an inset shadow on the first cell because a border on <tr> is unreliable
  // under border-collapse.
  const marker =
    isSelected
      ? 'var(--co-brass)'
      : sla.tone === 'danger' || sla.tone === 'warn'
        ? TONE_INK[sla.tone]
        : 'transparent';

  return (
    <tr
      onClick={onClick}
      className={clsx(
        'group cursor-pointer transition-colors duration-[120ms]',
        isSelected ? 'bg-[var(--co-brass-wash)]' : 'hover:bg-[var(--co-selected)]'
      )}
    >
      <td
        className="w-[34px] px-4 py-3 align-middle"
        style={{ boxShadow: `inset 2px 0 0 ${marker}` }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <SelectCheckbox checked={isSelected} onChange={onSelect} />
        </div>
      </td>

      <td className="max-w-0 px-4 py-3 align-middle">
        {/* Both lines are `truncate`d to keep the column narrow, so the full
            value is only available on hover — without the title attribute a
            ticket number reads as "#TKT-00..." and cannot be read at all. */}
        <div
          className={clsx(
            'truncate text-[13.5px] font-semibold',
            isArchived ? 'text-[var(--co-ink-3)]' : 'text-[var(--co-ink)]'
          )}
          title={ticket.subject}
        >
          {ticket.subject}
        </div>
        <div className="co-mono mt-1 flex items-center gap-[9px] truncate text-[10.5px] leading-none text-[var(--co-ink-3)]">
          <span className="flex-none" title={`#${ticket.ticketNumber || ticket.id.slice(0, 8)}`}>
            #{ticket.ticketNumber || ticket.id.slice(0, 8)}
          </span>
          {who && (
            <>
              <span aria-hidden="true">·</span>
              <span className="min-w-0 truncate uppercase">{who}</span>
            </>
          )}
          {channel && (
            <>
              <span aria-hidden="true">·</span>
              <span className="flex-none">{channel}</span>
            </>
          )}
        </div>
      </td>

      <td className="w-[148px] overflow-hidden px-4 py-3 align-middle xl:w-[168px]">
        {assignee ? (
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="flex h-7 w-7 flex-none items-center justify-center rounded-[7px] text-[11px] font-semibold text-[var(--co-ink-invert)]"
              style={{ background: identityColor(assignee) }}
              aria-hidden
            >
              {initials(assignee)}
            </span>
            {/* Two lines, then an ellipsis: one line cut "Ashutosh keynova" to
                "Ashutosh keyno…" in a 148px column. */}
            <span
              className="min-w-0 line-clamp-2 break-words text-[12.5px] leading-[1.3] text-[var(--co-ink-2)]"
              title={assignee}
            >
              {assignee}
            </span>
          </div>
        ) : (
          /* Unowned work should look unfinished — a dashed chip, not an em-dash. */
          <span className="co-mono inline-block rounded-md border border-dashed border-[var(--co-terracotta)] px-[6px] py-[4px] text-[9.5px] font-semibold uppercase leading-none tracking-[.1em] text-[var(--co-terracotta-ink)]">
            Unassigned
          </span>
        )}
        {secondaryBadge && (
          <span
            className="co-mono mt-1 inline-block rounded-md bg-[var(--co-brass-wash)] px-[6px] py-[3px] text-[9.5px] font-semibold uppercase leading-none tracking-[.08em] text-[var(--co-brass-ink)]"
            title={secondaryBadge === 'Secondary' ? 'You work this ticket as a secondary agent' : 'Secondary agents on this ticket'}
          >
            {secondaryBadge}
          </span>
        )}
      </td>

      <td className="w-[104px] overflow-hidden px-4 py-3 align-middle xl:w-[150px]">
        {/* Where the ticket came from: product (API key), channel, or agent. */}
        <TicketSourceBadge ticket={ticket} />
      </td>

      <td className="w-[104px] px-4 py-3 align-middle whitespace-nowrap">
        <PriorityBadge priority={ticket.priority} showLabel />
      </td>

      <td className="w-[140px] overflow-hidden px-4 py-3 align-middle xl:w-[186px]">
        {lane ? (
          <span
            title={lane}
            className="co-mono block truncate text-[11px] uppercase tracking-[.06em] text-[var(--co-ink-3)]"
          >
            {lane}
          </span>
        ) : (
          <span className="co-mono text-[11px] text-[var(--co-line-strong)]">—</span>
        )}
      </td>

      <td className="w-[116px] px-4 py-3 text-right align-middle whitespace-nowrap xl:w-[132px]">
        <div
          className="co-mono text-[11.5px] font-semibold tabular-nums"
          style={{ color: TONE_INK[sla.tone] }}
        >
          {sla.label}
        </div>
        {/* Which target the figure belongs to. Without it a resolution
            countdown on a ticket that already replied looks mis-sorted. */}
        {sla.dimension && (
          <div className="co-mono mt-[3px] text-[9px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
            {sla.dimension}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function TicketsPage() {
  // So a ticket's "← Tickets" link returns to this view.
  useEffect(() => rememberTicketsView('/tickets/list'), []);
  const navigate = useNavigate();
  const toast = useToast();
  // Current user id — resolves the "Assigned to me" filter to real query params.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentTenantId = useAuthStore((s) => s.user?.tenantId);
  const mayUnassign = useAuthStore((s) => canManageAssignees(s.user));

  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<TicketFilters>({});
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  // True once the pipeline list has loaded successfully; a failed load never
  // claims "no pipelines".
  const [pipelinesLoaded, setPipelinesLoaded] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>(ALL_PIPELINES);
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const savedRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Set once a link's filters (see filtersFromSearchParams) have been applied,
  // so the mount effect's saved default filter doesn't land on top of them.
  const openedFromLink = useRef(false);
  const {
    savedFilters,
    fetchSavedFilters,
    getDefaultFilter,
    setActiveFilter,
    trackUsage,
  } = useSavedFiltersStore();

  const {
    tickets,
    total,
    page,
    limit,
    totalPages,
    isLoading,
    error,
    filters: storeFilters,
    fetchTickets,
    fetchAllForExport,
    setFilters,
    setPage,
    setLimit,
  } = useTicketsListStore();

  // Selection handlers
  const handleSelectTicket = useCallback((ticketId: string, selected: boolean) => {
    setSelectedTickets((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(ticketId);
      } else {
        next.delete(ticketId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map((t) => t.id)));
    }
  }, [tickets, selectedTickets.size]);

  const handleClearSelection = useCallback(() => {
    setSelectedTickets(new Set());
  }, []);

  const handleBulkAction = useCallback(
    async (actionId: string, value?: string | string[]) => {
      if (selectedTickets.size === 0) return;

      // Handle merge action specially - open modal instead of processing
      if (actionId === 'merge') {
        if (selectedTickets.size < 2) {
          console.warn('Select at least 2 tickets to merge');
          return;
        }
        setIsMergeModalOpen(true);
        return;
      }

      // Delete is destructive and irreversible — confirm before processing,
      // same pattern as the single-ticket delete on the detail page.
      if (actionId === 'delete') {
        setIsBulkDeleteConfirmOpen(true);
        return;
      }

      setIsBulkProcessing(true);
      try {
        const ticketIds = Array.from(selectedTickets);

        switch (actionId) {
          case 'status':
            if (typeof value === 'string') {
              await Promise.all(ticketIds.map((id) => ticketService.updateStatus(id, value)));
            }
            break;

          case 'priority':
            if (typeof value === 'string') {
              await Promise.all(ticketIds.map((id) => ticketService.updatePriority(id, value)));
            }
            break;

          case 'assign':
            if (typeof value === 'string') {
              if (value === 'unassign') {
                // Handle unassign separately
                await Promise.all(ticketIds.map((id) => ticketService.assignTicket(id, null)));
              } else {
                // Assign to a specific agent
                await Promise.all(ticketIds.map((id) => ticketService.assignTicket(id, value)));
              }
            }
            break;

          case 'tags': {
            // value is the list of tags picked in the BulkActionsBar popover
            const tags = Array.isArray(value) ? value : value ? [value] : [];
            if (tags.length > 0) {
              await Promise.all(ticketIds.map((id) => ticketService.addTags(id, tags)));
            }
            break;
          }

          default:
            console.log(`Unknown bulk action: ${actionId}`, { ticketIds, value });
        }

        // Clear selection and refresh after successful action
        setSelectedTickets(new Set());
        fetchTickets();
      } catch (err) {
        console.error('Bulk action failed:', err);
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [selectedTickets, fetchTickets],
  );

  const handleMergeTickets = useCallback(
    async (primaryTicketId: string, secondaryTicketIds: string[]) => {
      if (secondaryTicketIds.length === 0) return;
      setIsMerging(true);
      // #18941 — this used to be a stub (console.log + a fake delay) and sent
      // no request. Merge each selected secondary into the primary via the
      // real endpoint, one at a time: each pair is its own transaction, so a
      // mid-way failure must report how many already merged.
      let mergedCount = 0;
      try {
        for (const secondaryTicketId of secondaryTicketIds) {
          await ticketService.mergeTickets(primaryTicketId, [secondaryTicketId]);
          mergedCount++;
        }

        setIsMergeModalOpen(false);
        setSelectedTickets(new Set());
        toast.success(
          'Tickets merged',
          `${secondaryTicketIds.length} ticket${secondaryTicketIds.length > 1 ? 's' : ''} merged into the primary and closed.`,
        );
      } catch (err: any) {
        const reason =
          err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.message ||
          'Could not merge the selected tickets. Please try again.';
        toast.error(
          'Merge failed',
          mergedCount > 0
            ? `${mergedCount} of ${secondaryTicketIds.length} merged before it stopped. ${reason}`
            : reason,
        );
        if (mergedCount > 0) {
          setIsMergeModalOpen(false);
          setSelectedTickets(new Set());
        }
      } finally {
        setIsMerging(false);
        // Refresh on partial success too, so the closed/merged tickets drop off.
        if (mergedCount > 0) fetchTickets();
      }
    },
    [fetchTickets, toast],
  );

  const handleBulkDeleteTickets = useCallback(async () => {
    if (selectedTickets.size === 0) return;
    const ticketIds = Array.from(selectedTickets);
    setIsBulkDeleting(true);
    try {
      await Promise.all(ticketIds.map((id) => ticketService.deleteTicket(id)));
      setIsBulkDeleteConfirmOpen(false);
      setSelectedTickets(new Set());
      toast.success(
        'Tickets deleted',
        `${ticketIds.length} ticket${ticketIds.length > 1 ? 's' : ''} deleted.`,
      );
      fetchTickets();
    } catch (err: any) {
      toast.error(
        'Delete failed',
        err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.message ||
          'Could not delete the selected tickets. Please try again.',
      );
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedTickets, fetchTickets, toast]);

  const isAllSelected = tickets.length > 0 && selectedTickets.size === tickets.length;
  const isSomeSelected = selectedTickets.size > 0 && selectedTickets.size < tickets.length;

  // Get selected tickets as an array for merge modal
  const selectedTicketsList = useMemo(
    () => tickets.filter((t) => selectedTickets.has(t.id)),
    [tickets, selectedTickets],
  );

  // Export column definitions
  const ticketExportColumns = useMemo<ExportColumn<Ticket>[]>(
    () => [
      { key: 'ticketNumber', header: 'Ticket #' },
      { key: 'subject', header: 'Subject' },
      { key: 'status', header: 'Status' },
      { key: 'priority', header: 'Priority' },
      { key: 'channel', header: 'Channel' },
      {
        key: 'origin',
        header: 'Source',
        formatter: (_value, row) => getTicketSourceDisplay(row)?.label || '',
      },
      {
        key: 'ticketTypeId',
        header: 'Ticket Type',
        formatter: (_value, row) => (row as any).ticketType?.name || '',
      },
      { key: 'contactName', header: 'Customer Name' },
      { key: 'contactEmail', header: 'Customer Email' },
      {
        key: 'assigneeId',
        header: 'Assigned To',
        formatter: (_value, row) => {
          if (row.assignee?.name) return row.assignee.name;
          if (row.assignee?.email) return row.assignee.email;
          return row.assigneeId ? '—' : 'Unassigned';
        },
      },
      { key: 'createdAt', header: 'Created', formatter: (value) => formatters.dateTime(value) },
      { key: 'updatedAt', header: 'Updated', formatter: (value) => formatters.dateTime(value) },
      { key: 'tags', header: 'Tags', formatter: (value) => formatters.array(value) },
    ],
    [],
  );

  // Count active advanced filters. includeSubTeams only qualifies the Team
  // filter; it is not a filter of its own.
  const activeAdvancedFilterCount = Object.entries(advancedFilters).filter(
    ([k, v]) => k !== 'includeSubTeams' && v !== undefined && (Array.isArray(v) ? v.length > 0 : true),
  ).length;

  // On mount: load saved filters, then apply the default one if the user has
  // set one. If there's no default, the store keeps its empty filters so the
  // list shows ALL non-backtracked tickets (backtracked are excluded
  // server-side by default). We decide the filters BEFORE fetching so the rows
  // and the header count always reflect the default filter (or the full set) —
  // the original code fetched first and could leave a stale count.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchSavedFilters();
      if (cancelled) return;

      const defaultFilter = openedFromLink.current ? undefined : getDefaultFilter();
      if (defaultFilter) {
        const f = defaultFilter.filters as Record<string, any>;
        // Back-compat: older saved filters stored single `assigneeId` /
        // `ticketTypeId`; the panel now uses multi-select arrays.
        const assigneeIds: string[] | undefined =
          f.assigneeIds ?? (f.assigneeId ? [f.assigneeId] : undefined);
        const ticketTypeIds: string[] | undefined =
          f.ticketTypeIds ?? (f.ticketTypeId ? [f.ticketTypeId] : undefined);
        const advanced = {
          ...f,
          assigneeIds,
          assigneeId: undefined,
          ticketTypeIds,
          ticketTypeId: undefined,
        } as TicketFilters;
        setAdvancedFilters(advanced);
        if (f.search) setSearchQuery(f.search);
        setActiveFilter(defaultFilter.id);
        trackUsage(defaultFilter.id);
        // Synchronous zustand update — fetchTickets() below reads these via get().
        // Full filter set, same helper the board and Apply path use.
        setFilters({
          ...FILTER_RESET_KEYS,
          ...buildApiFilters(advanced, f.search || '', currentUserId),
        });
      }

      // The register opens newest-first so a just-created ticket is the top row;
      // clicking the SLA header still ranks by deadline (`sla_priority`). Set
      // here rather than in the store's DEFAULT_FILTERS, because the Board reads
      // the same store and its column order is not this page's business.
      setFilters({ sortBy: 'createdAt', sortOrder: 'desc' });

      // Fetch after filters are decided: default filter applied, or full
      // non-backtracked set when no default is set.
      fetchTickets();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchTickets]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the popovers on an outside click, the way the Inbox's filter panel does.
  useEffect(() => {
    if (!showFilters && !showSavedViews) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      // AdvancedFilters' dropdown menus are portalled to <body>, outside
      // filterRef; closing on them unmounted the panel before the click landed.
      const inFilterMenu = t instanceof Element && t.closest('[data-filter-menu]');
      if (showFilters && !inFilterMenu && filterRef.current && !filterRef.current.contains(t)) {
        setShowFilters(false);
      }
      if (showSavedViews && savedRef.current && !savedRef.current.contains(t)) {
        setShowSavedViews(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showFilters, showSavedViews]);

  const handleAdvancedFilters = useCallback(
    (filters: TicketFilters) => {
      setAdvancedFilters(filters);
      // Forward the FULL filter set to the API (status, priority, channel, tags,
      // assignee, ticket type, date range, SLA). FILTER_RESET_KEYS clears the
      // previous apply first — setFilters merges into the store, so an omitted key
      // would otherwise linger and a cleared filter would never actually clear.
      setFilters({
        ...FILTER_RESET_KEYS,
        ...buildApiFilters(filters, searchQuery, currentUserId),
      });
      setSelectedTickets(new Set());
      fetchTickets();
    },
    [searchQuery, currentUserId, setFilters, fetchTickets],
  );

  // A link can open the list pre-filtered (the nav rail's Tickets badge opens
  // "mine, waiting on me"). Applied through the panel's own path so the panel
  // and tabs show it, whether the page is mounting or already open; the params
  // are then dropped so they don't pin the filters on reload.
  useEffect(() => {
    const fromLink = filtersFromSearchParams(searchParams);
    if (!fromLink) return;
    openedFromLink.current = true;
    setActiveFilter(null);
    handleAdvancedFilters(fromLink);
    setSearchParams({}, { replace: true });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // The cohort tabs write into the same filter object the panel edits, so there
  // is exactly one source of truth for "what is this list showing".
  const tab = activeTab(advancedFilters);
  const selectTab = useCallback(
    (next: TicketTab) => handleAdvancedFilters(applyTab(advancedFilters, next)),
    [advancedFilters, handleAdvancedFilters],
  );

  /**
   * Cohort sizes for the tab strip.
   *
   * Each count is the SAME query the tab itself runs, asked for one row and
   * read off `total` — so the number on a tab is always exactly what clicking
   * it will show, including the panel's other filters and the pipeline scope.
   * A count that fails renders as nothing rather than 0.
   */
  // The COMMITTED search, not the input box — counts that moved on every
  // keystroke would fire five requests per character and disagree with a list
  // that only updates on submit.
  const committedSearch = storeFilters.search || '';
  const countBase = useMemo(
    () => ({
      ...buildApiFilters(advancedFilters, committedSearch, currentUserId),
      pipelineId: selectedPipelineId === ALL_PIPELINES ? undefined : selectedPipelineId,
    }),
    [advancedFilters, committedSearch, currentUserId, selectedPipelineId],
  );

  const { data: tabCounts } = useQuery({
    queryKey: ['tickets', 'tabCounts', countBase, advancedFilters] as const,
    staleTime: 30_000,
    queryFn: async () => {
      const entries = await Promise.all(
        TICKET_TABS.map(async (t) => {
          const forTab = applyTab(advancedFilters, t);
          const params = {
            ...FILTER_RESET_KEYS,
            ...buildApiFilters(forTab, committedSearch, currentUserId),
            pipelineId: countBase.pipelineId,
            page: 1,
            limit: 1,
          };
          try {
            const res = await ticketService.getTickets(params as any);
            return [t, res.total] as const;
          } catch {
            return [t, null] as const;
          }
        }),
      );
      return Object.fromEntries(entries) as Record<TicketTab, number | null>;
    },
  });

  // Load pipelines for the list-view pipeline selector. Failure is non-fatal —
  // the dropdown just won't render, leaving the list unscoped ("All").
  useEffect(() => {
    let cancelled = false;
    pipelineService
      .getPipelines()
      .then((data) => {
        if (!cancelled) {
          setPipelines(data);
          setPipelinesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setPipelines([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Scope the list (and therefore the export, which reuses the store filters) by
  // the chosen pipeline. 'all' clears the pipeline filter so every pipeline shows.
  const handlePipelineChange = useCallback(
    (id: string) => {
      setSelectedPipelineId(id);
      setFilters({ pipelineId: id === ALL_PIPELINES ? undefined : id });
      fetchTickets();
    },
    [setFilters, fetchTickets],
  );

  const submitSearch = useCallback(
    (value: string) => {
      setFilters({ search: value || undefined });
      fetchTickets();
    },
    [setFilters, fetchTickets],
  );

  // Bug #9011: the input had no visible submit affordance (no icon/button),
  // so typing silently did nothing until Enter was pressed - it looked broken
  // rather than deliberate. Debounce-on-type matches every other list page
  // (Customers, Knowledge Base, Inbox); the guard against `committedSearch`
  // (not `searchQuery`) skips a redundant fetch when the debounced value
  // already matches what's committed, e.g. on mount/tab-restore.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== committedSearch) {
        submitSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const applySavedView = useCallback(
    (id: string) => {
      const view = savedFilters.find((v) => v.id === id);
      setShowSavedViews(false);
      if (!view) return;
      const f = view.filters as Record<string, any>;
      const advanced = {
        ...f,
        assigneeIds: f.assigneeIds ?? (f.assigneeId ? [f.assigneeId] : undefined),
        assigneeId: undefined,
        ticketTypeIds: f.ticketTypeIds ?? (f.ticketTypeId ? [f.ticketTypeId] : undefined),
        ticketTypeId: undefined,
      } as TicketFilters;
      setSearchQuery(f.search || '');
      setActiveFilter(view.id);
      trackUsage(view.id);
      handleAdvancedFilters(advanced);
    },
    [savedFilters, setActiveFilter, trackUsage, handleAdvancedFilters],
  );

  const handleTicketClick = (ticket: Ticket) => {
    navigate(`/tickets/${ticket.id}`);
  };

  const SORT_LABEL: Record<string, string> = {
    sla_priority: 'SLA soonest',
    subject: 'Subject',
    priority: 'Priority',
    updatedAt: 'Last updated',
    createdAt: 'Newest',
  };
  const sortLabel = SORT_LABEL[storeFilters.sortBy || 'updatedAt'] || storeFilters.sortBy;

  // The subline states the scale of the register, scoped to what the filters
  // actually select. `total` is the server's count for this query.
  const breachedOnPage = tickets.filter((t) => liveSla(t).tone === 'danger').length;
  const subtitle = [
    `${total} matching`,
    tabCounts?.unassigned != null ? `${tabCounts.unassigned} unassigned` : null,
    tabCounts?.breached != null ? `${tabCounts.breached} breached` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card ---------- */}
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={HEADER_CARD}>
        {/* Row 1: title block + the utility cluster, nothing else. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.08] tracking-[-.018em] text-[var(--co-ink)]">
              Tickets
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {subtitle}
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: every other header control, wrapping when space runs out. */}
        <div className="mt-3 flex items-start gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          {/* Filters wrap among themselves on the left; the actions keep the
              top-right corner at every width. */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[10px]">
            {pipelines.length > 0 && (
              <PipelineSelect
                value={selectedPipelineId}
                onChange={handlePipelineChange}
                options={[
                  { value: ALL_PIPELINES, label: 'All pipelines' },
                  ...pipelines.map((p) => ({ value: p.id, label: p.name })),
                ]}
                className={CONTROL}
              />
            )}

            {/* Saved views — the filters this tenant already stored. */}
            <div className="relative" ref={savedRef}>
              <button
                type="button"
                onClick={() => setShowSavedViews((v) => !v)}
                disabled={savedFilters.length === 0}
                title={
                  savedFilters.length === 0
                    ? 'No saved views yet'
                    : 'Apply a saved view'
                }
                className={CONTROL}
              >
                <Bookmark className="h-[14px] w-[14px]" />
                Saved views
                <ChevronDown className="h-[13px] w-[13px] text-[var(--co-ink-3)]" />
              </button>
              {showSavedViews && savedFilters.length > 0 && (
                <div
                  className={clsx(
                    CARD_CLASS,
                    'absolute left-0 top-full z-50 mt-2 max-h-[320px] w-[260px] overflow-y-auto py-1'
                  )}
                  style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                >
                  {savedFilters.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => applySavedView(v.id)}
                      className="flex w-full items-baseline gap-2 px-4 py-2 text-left hover:bg-[var(--co-brass-wash)]"
                    >
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--co-ink-2)]">
                        {v.name}
                      </span>
                      {v.isDefault && (
                        <span className="co-mono flex-none text-[9px] uppercase tracking-[.1em] text-[var(--co-brass-ink)]">
                          Default
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="relative" ref={filterRef}>
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={clsx(
                  CONTROL,
                  (showFilters || activeAdvancedFilterCount > 0) &&
                    'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
                )}
              >
                <SlidersHorizontal className="h-[14px] w-[14px]" />
                Filters
                {activeAdvancedFilterCount > 0 && (
                  <span className="co-mono text-[10px] font-semibold text-[var(--co-brass-ink)]">
                    {activeAdvancedFilterCount}
                  </span>
                )}
              </button>
              {showFilters && (
                <div className="absolute left-0 top-full z-50 mt-2 w-[min(92vw,44rem)]">
                  <AdvancedFilters
                    filters={advancedFilters}
                    onChange={handleAdvancedFilters}
                    onClose={() => setShowFilters(false)}
                    currentUserId={currentUserId}
                  />
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(searchQuery);
              }}
              className="relative max-w-[240px] flex-[1_1_160px]"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--co-ink-4)"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
                className="pointer-events-none absolute left-[10px] top-1/2 -translate-y-1/2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="text"
                placeholder="Search the register…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search tickets"
                className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[31px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--co-ink-4)] hover:text-[var(--co-ink)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </form>
            {sortLabel && (
              <span className={clsx(CONTROL, 'pointer-events-none')}>
                <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                  Sort
                </span>
                {sortLabel}
              </span>
            )}

            <div className="flex-1" />

            </div>
            <div className="flex flex-none items-center gap-[10px]">
            <ExportButton
              onFetchData={fetchAllForExport}
              columns={ticketExportColumns}
              filename="tickets"
              sheetName="Tickets"
              variant="ghost"
              size="sm"
              className={`${CONTROL} !rounded-[10px]`}
            />

            <button
              type="button"
              onClick={() => navigate('/tickets')}
              className={CONTROL}
              title="Switch to board view"
            >
              <LayoutGrid className="h-[14px] w-[14px]" />
              Board
            </button>

            <button
              type="button"
              onClick={() => fetchTickets()}
              disabled={isLoading}
              className={CONTROL}
              aria-label="Refresh"
            >
              <RotateCw className={clsx('h-[14px] w-[14px]', isLoading && 'animate-spin')} />
            </button>

            <button type="button" onClick={() => navigate('/tickets/new')} className={HEADER_PRIMARY}>
              + New ticket
            </button>
            </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
            {TICKET_TABS.map((t) => {
              const active = tab === t;
              const count = tabCounts?.[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => selectTab(t)}
                  aria-pressed={active}
                  className={clsx(
                    'co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]',
                    active
                      ? 'bg-[var(--co-raised)] font-semibold text-[var(--co-ink)] shadow-[0_1px_2px_rgba(35,32,28,.08)]'
                      : 'font-medium text-[var(--co-ink-3)]'
                  )}
                >
                  {TAB_LABEL[t]}
                  {count != null && (
                    <span
                      className="font-semibold"
                      style={{
                        color: active ? 'var(--co-brass-ink)' : 'var(--co-ink-3-soft)',
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            {tab === null && (
              <span className="co-mono px-2 text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
                Custom filter
              </span>
            )}
          </div>

        </div>
      </header>

      {/* Error */}
      {error && (
        <div
          className="flex flex-none items-center gap-3 rounded-lg border p-4"
          style={{
            borderColor: 'var(--co-burgundy-line)',
            background: 'var(--co-burgundy-wash)',
          }}
        >
          <AlertCircle className="h-[18px] w-[18px] flex-shrink-0 text-[var(--co-burgundy)]" />
          <p className="text-[13px] text-[var(--co-burgundy)]">{error}</p>
        </div>
      )}

      {/* ---------- the register ---------- */}
      <section
        className={clsx(CARD_CLASS, 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden')}
        style={CARD}
      >
        {/* Bulk bar — inset in the card, not a fourth floating layer. */}
        <BulkActionsBar
          selectedCount={selectedTickets.size}
          totalCount={tickets.length}
          matchingTotal={total}
          onSelectAll={() => setSelectedTickets(new Set(tickets.map((t) => t.id)))}
          onClearSelection={handleClearSelection}
          onAction={handleBulkAction}
          isProcessing={isBulkProcessing}
          canUnassign={mayUnassign}
        />

        {isLoading && tickets.length === 0 ? (
          <div className="p-12 text-center">
            <RefreshCw className="mx-auto mb-4 h-6 w-6 animate-spin text-[var(--co-brass)]" />
            <p className="co-mono text-[10.5px] uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Loading tickets
            </p>
          </div>
        ) : tickets.length === 0 && pipelinesLoaded && pipelines.length === 0 ? (
          // No pipeline exists yet: tickets need one, so point at creating it.
          <div className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-lg border border-[var(--co-line-strong)]">
              <GitBranch className="h-6 w-6 text-[var(--co-ink-4)]" />
            </div>
            <h3 className="text-[24px] font-bold leading-[1.1] tracking-[-.02em] text-[var(--co-ink)]">
              No pipelines yet
            </h3>
            <p
              className="mb-7 mt-2 max-w-[46ch] text-[13.5px] leading-[1.6] text-[var(--co-ink-3)]"
              style={{ textWrap: 'pretty' } as React.CSSProperties}
            >
              Every ticket needs a pipeline. Create your first one and its default stages are set up with it.
            </p>
            <button
              type="button"
              onClick={() => navigate(pipelinesSettingsTarget(currentTenantId, { create: true }))}
              className={PRIMARY}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create your first pipeline
            </button>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-lg border border-[var(--co-line-strong)]">
              <Mail className="h-6 w-6 text-[var(--co-ink-4)]" />
            </div>
            <h3 className="text-[24px] font-bold leading-[1.1] tracking-[-.02em] text-[var(--co-ink)]">
              Nothing in the register
            </h3>
            <p
              className="mb-7 mt-2 max-w-[46ch] text-[13.5px] leading-[1.6] text-[var(--co-ink-3)]"
              style={{ textWrap: 'pretty' } as React.CSSProperties}
            >
              No tickets match the current filters. Clear them, or open the first one yourself.
            </p>
            <button
              type="button"
              onClick={() => navigate('/tickets/new')}
              className={PRIMARY}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create ticket
            </button>
          </div>
        ) : (
          <div className="min-h-0 min-w-0 flex-1 overflow-auto">
            <table className="w-full min-w-[900px] table-fixed border-collapse">
              {/* Sticky header pinned to the scroll container (matches the
                  proven BacktrackPage pattern). Opaque per-cell backgrounds
                  keep scrolling rows from showing through. */}
              <thead className="sticky top-0 z-20">
                <tr className="[&>th]:border-b [&>th]:border-[var(--co-line)] [&>th]:bg-[var(--co-raised)]">
                  <th className="w-[34px] px-4 pb-[11px] pt-[14px] align-middle">
                    <SelectCheckbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <SortableHeader field="subject" label="Subject" />
                  <PlainHeader label="Assignee" className="w-[148px] xl:w-[168px]" />
                  <PlainHeader label="Source" className="w-[104px] xl:w-[150px]" />
                  <SortableHeader field="priority" label="Priority" className="w-[104px]" />
                  <PlainHeader label="Pipeline · stage" className="w-[140px] xl:w-[186px]" />
                  {/* The arrow sits on the column the rows are actually
                      ordered by — `sla_priority` on the server. */}
                  <SortableHeader
                    field="sla_priority"
                    label="SLA"
                    ascOnly
                    className="w-[116px] !text-right xl:w-[132px] [&>button]:justify-end [&>button]:w-full"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--co-line-subtle)]">
                {tickets.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    onClick={() => handleTicketClick(ticket)}
                    isSelected={selectedTickets.has(ticket.id)}
                    onSelect={(selected) => handleSelectTicket(ticket.id, selected)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Foot band: what is on screen, and what the order actually means.
            One row — the pager is an inline cluster (kit `Pager`) rather than
            @centraoneplus/ui's <Pagination>, which carries its own white bar
            and doubled the height of this band. */}
        {tickets.length > 0 && (
          <div
            className="flex flex-none flex-wrap items-center gap-x-[14px] gap-y-2 border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-[6px]"
            style={{ borderRadius: '0 0 9px 9px' }}
          >
            <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {tickets.length} of {total} shown
              {breachedOnPage > 0 ? ` · ${breachedOnPage} breached here` : ''}
            </span>
            <div className="flex-1" />
            {totalPages > 1 ? (
              <Pager
                id="tickets"
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                pageSize={limit}
                onPageChange={setPage}
                onPageSizeChange={setLimit}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            ) : storeFilters.sortBy === 'sla_priority' ? (
              <span className="text-[12px] text-[var(--co-ink-3)]">
                Sorted by SLA deadline — the top row is the one that costs most, not the newest.
              </span>
            ) : (
              <span className="text-[12px] text-[var(--co-ink-3)]">
                Sorted by {sortLabel?.toLowerCase()} — click SLA to rank by deadline instead.
              </span>
            )}
          </div>
        )}
      </section>

      {/* Merge Tickets Modal */}
      <TicketMergeModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        selectedTickets={selectedTicketsList}
        onMerge={handleMergeTickets}
        isLoading={isMerging}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isBulkDeleteConfirmOpen}
        onClose={() => {
          if (!isBulkDeleting) setIsBulkDeleteConfirmOpen(false);
        }}
        onConfirm={handleBulkDeleteTickets}
        title="Delete tickets"
        message={`Delete ${selectedTickets.size} ticket${selectedTickets.size > 1 ? 's' : ''}? This permanently removes the ticket${selectedTickets.size > 1 ? 's' : ''} and their conversations. This cannot be undone.`}
        confirmLabel="Delete tickets"
        cancelLabel="Cancel"
        variant="danger"
        loading={isBulkDeleting}
      />
    </div>
  );
}
